import {
  useLicensingServiceV1,
  useUserManagementServiceV1,
} from "@rw1/api-services";
import { Licensing_V1, UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAllUserGroupsForACompany } from "../../../hooks/useAllUserGroupsForACompany";
import { LICENSE_GROUP_ALLOCATIONS_QUERY_KEY } from "../../../hooks/useExternalExperiences";
import { fetchAllPages, useFetchAll } from "../../../hooks/useFetchAll";
import { useLocalization } from "../../../hooks/useLocalization";
import { useUsers } from "../../users/hooks/useUsers";
import { TAccessSource, TExperienceUserAccessRow } from "../types";
import { useIncomingLicenses } from "./useIncomingLicenses";
import { useLicenseUsers } from "./useLicenseUsers";

type UserWithCompanyName = UserManagement_V1.UserWithCompanyName;

const createUserRow = (
  userId: string,
  fullName: string,
  email: string,
): TExperienceUserAccessRow => {
  return {
    userId,
    fullName,
    email,
    sources: [],
    longestExpiry: null,
    hasDirectAccess: false,
    hasGroupAccess: false,
  };
};

const getUserDisplayName = (
  user: UserWithCompanyName | undefined,
  t: (key: string) => string,
): string => {
  return user
    ? `${user.surname}, ${user.forename}`
    : t("experiences.unknownUser");
};

const updateLongestExpiry = (
  current: string | null,
  next: string | null,
): string | null => {
  if (next === null) return null;
  if (current === null) return next;

  return next > current ? next : current;
};

interface UseExternalExperienceUsersParams {
  releaseChannelId?: string;
  companyId?: string;
  enabled?: boolean;
}

export const useExternalExperienceUsers = ({
  releaseChannelId,
  companyId,
  enabled = true,
}: UseExternalExperienceUsersParams): {
  loading: boolean;
  rows: TExperienceUserAccessRow[];
} => {
  const { t } = useLocalization();
  const licensingService = useLicensingServiceV1();
  const userManagementService = useUserManagementServiceV1();

  const { licensesByReleaseChannel, isLoading: licenseIsLoading } =
    useIncomingLicenses();

  const license = useMemo(() => {
    if (!releaseChannelId) return null;

    return licensesByReleaseChannel.get(releaseChannelId) ?? null;
  }, [releaseChannelId, licensesByReleaseChannel]);

  const { users: licenseAllocations, isLoading: usersLoading } =
    useLicenseUsers(license?.licenseId);

  // Fetch Group Allocations
  const { data: groupAllocations = [], isLoading: loadingGroupAllocations } =
    useFetchAll<Licensing_V1.LicenseGroupAllocation>(
      [LICENSE_GROUP_ALLOCATIONS_QUERY_KEY, license?.licenseId],
      (page, size) =>
        licensingService.getLicenseGroupAllocations(
          license!.licenseId,
          page,
          size,
        ),
      {
        enabled: !!license?.licenseId && enabled,
        staleTime: 1000 * 60 * 5,
      },
    );

  // Fetch Users for each Group
  const groupUsersQueries = useQueries({
    queries: groupAllocations.map((ga) => ({
      queryKey: ["users-in-group", ga.userGroupId],
      queryFn: () =>
        fetchAllPages(
          (page, size) =>
            userManagementService.getUsersForUserGroup(
              ga.userGroupId,
              page,
              size,
              undefined,
            ),
          1000,
        ),
      enabled: !!ga.userGroupId && !ga.isRevoked && enabled,
      staleTime: 1000 * 60 * 5,
    })),
  });

  // Fetch All Groups to get Names
  const { groups: allGroups, isLoading: loadingAllGroups } =
    useAllUserGroupsForACompany(companyId, true);

  const groupsMap = useMemo(() => {
    const map = new Map<string, string>();
    allGroups.forEach((g) => map.set(g.userGroupId, g.userGroupName));

    return map;
  }, [allGroups]);

  // Fetch All Company Users (for Direct Allocations hydration)
  const { users: allCompanyUsers, isLoading: loadingCompanyUsers } = useUsers({
    companyId,
    enabled: !!companyId && enabled,
  });

  const rows = useMemo(() => {
    const byUser = new Map<string, TExperienceUserAccessRow>();
    const userMap = new Map<string, UserWithCompanyName>();

    // Index company users
    for (const user of allCompanyUsers) {
      userMap.set(user.userId, user);
    }

    // Process Direct Allocations
    licenseAllocations.forEach((allocation) => {
      const userId = allocation.userId;
      if (!userId) return;

      const user = userMap.get(userId);
      if (!user) return; // Only show active users

      const fullName = getUserDisplayName(user, t);
      const email = user.emailAddress;

      let existing = byUser.get(userId);
      if (!existing) {
        existing = createUserRow(userId, fullName, email);
        byUser.set(userId, existing);
      }

      const expiresAtStr = allocation.expiresAt
        ? allocation.expiresAt.toISOString()
        : null;

      const source: TAccessSource = {
        id: `license-${userId}`,
        type: "DIRECT",
        name: t("experiences.licenseAllocation"),
        expiresAt: expiresAtStr,
      };

      existing.sources.push(source);
      existing.longestExpiry = updateLongestExpiry(
        existing.longestExpiry,
        source.expiresAt,
      );
      existing.hasDirectAccess = true;
    });

    // Process Group Allocations
    groupAllocations.forEach((ga, index) => {
      if (!ga.userGroupId || ga.isRevoked) return;

      const usersInGroup = groupUsersQueries[index]?.data ?? [];
      const groupName =
        groupsMap.get(ga.userGroupId) ?? t("experiences.unknownGroup");
      const expiresAtStr = ga.expiresAt ? ga.expiresAt.toISOString() : null;

      usersInGroup.forEach((groupUser) => {
        const user = userMap.get(groupUser.userId) ?? groupUser;
        const userId = user.userId;

        let existing = byUser.get(userId);
        if (!existing) {
          existing = createUserRow(
            userId,
            getUserDisplayName(user, t),
            user.emailAddress,
          );
          byUser.set(userId, existing);
        }

        // Avoid duplicates if user is in multiple groups pointing to this license
        if (!existing.sources.find((s) => s.id === ga.userGroupId)) {
          const source: TAccessSource = {
            id: ga.userGroupId,
            type: "GROUP",
            name: groupName,
            expiresAt: expiresAtStr,
          };

          existing.sources.push(source);
          existing.longestExpiry = updateLongestExpiry(
            existing.longestExpiry,
            expiresAtStr,
          );
          existing.hasGroupAccess = true;
        }
      });
    });

    return Array.from(byUser.values());
  }, [
    licenseAllocations,
    groupAllocations,
    allCompanyUsers,
    t,
    groupUsersQueries,
    groupsMap,
  ]);

  const loadingGroupUsers = groupUsersQueries.some((q) => q.isLoading);

  return {
    loading:
      licenseIsLoading ||
      usersLoading ||
      loadingGroupAllocations ||
      loadingGroupUsers ||
      loadingCompanyUsers ||
      loadingAllGroups,
    rows,
  };
};
